import { createAuthAxios } from './auth';

// Private helper functions
// INBOUND: Messages FROM QuePasa TO Chatwoot (QuePasa → N8N → Chatwoot)
const syncInboundWebhook = async (accountId, inboxId) => {
  const http = createAuthAxios();
  const response = await http.post(
    `/api/v1/accounts/${accountId}/quepasa/sync_inbound_webhook`,
    { inbox_id: inboxId }
  );
  // Return webhook_status.to_chatwoot as the result
  return response.data.webhook_status?.to_chatwoot || response.data;
};

// OUTBOUND: Messages FROM Chatwoot TO QuePasa (Chatwoot → N8N → QuePasa)
const syncOutboundWebhook = async (accountId, inboxId) => {
  const http = createAuthAxios();
  const response = await http.post(
    `/api/v1/accounts/${accountId}/quepasa/sync_outbound_webhook`,
    { inbox_id: inboxId }
  );
  // Return webhook_status.from_chatwoot as the result
  return response.data.webhook_status?.from_chatwoot || response.data;
};

const webhookService = {
  /**
   * Validates current webhook configuration (to_chatwoot + from_chatwoot)
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @returns {Promise<{to_chatwoot: Object, from_chatwoot: Object}>} Webhook status with configured/valid flags and error details
   * @example
   * const status = await quepasa.service.webhook.validate(1, 803);
   * console.log(status.to_chatwoot.valid); // true/false
   */
  async validate(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.get(
      `/api/v1/accounts/${accountId}/quepasa/validate_webhook`,
      { params: { inbox_id: inboxId } }
    );
    return response.data;
  },

  /**
   * Creates a new webhook in QuePasa with custom trackid and extras
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @returns {Promise<{success: boolean, data: Object}>} Creation result
   * @example
   * await quepasa.service.webhook.create(1, 803);
   */
  async create(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/webhook/create`,
      { inbox_id: inboxId }
    );
    return response.data;
  },

  /**
   * Tests webhook delivery by sending a test message
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @param {string} [direction='to_chatwoot'] - Direction: "to_chatwoot" (incoming) or "from_chatwoot" (outgoing)
   * @returns {Promise<{success: boolean}>} Test result
   * @example
   * await quepasa.service.webhook.test(1, 803, 'to_chatwoot');
   */
  async test(accountId, inboxId, direction = 'to_chatwoot') {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/webhook/test`,
      { inbox_id: inboxId, direction }
    );
    return response.data;
  },

  /**
   * Deletes webhook from QuePasa by trackid or URL
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @returns {Promise<{success: boolean}>} Deletion result
   * @example
   * await quepasa.service.webhook.delete(1, 803);
   */
  async delete(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.delete(
      `/api/v1/accounts/${accountId}/quepasa/webhook`,
      { params: { inbox_id: inboxId } }
    );
    return response.data;
  },

  /**
   * Updates webhook configuration using N8N middleware (legacy method)
   * Sets up bidirectional webhook with extras parameters
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @returns {Promise<{success: boolean, data: Object}>} Update result
   * @example
   * await quepasa.service.webhook.update(1, 803);
   */
  async update(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/webhook`,
      { inbox_id: inboxId }
    );
    return response.data;
  },

  /**
   * Syncs webhook in one or both directions (RECOMMENDED)
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @param {string|null} [direction=null] - Direction: "to" (QuePasa→Chatwoot), "from" (Chatwoot→QuePasa), null (both)
   * @returns {Promise<{to?: Object, from?: Object}>} Sync results
   * @example
   * // Sync both directions
   * await quepasa.service.webhook.configure(1, 803);
   * // Sync only incoming messages
   * await quepasa.service.webhook.configure(1, 803, 'to');
   * // Sync only outgoing messages
   * await quepasa.service.webhook.configure(1, 803, 'from');
   */
  async configure(accountId, inboxId, direction = null) {
    const results = {};

    // If direction is null or 'to', sync to QuePasa
    if (!direction || direction === 'to') {
      results.to = await syncInboundWebhook(accountId, inboxId);
    }

    // If direction is null or 'from', sync from QuePasa
    if (!direction || direction === 'from') {
      results.from = await syncOutboundWebhook(accountId, inboxId);
    }

    return results;
  }
};

// Add help properties to each method
webhookService.validate.help = 'validate(accountId, inboxId) - Validates webhook configuration (to_chatwoot + from_chatwoot). Returns status with configured/valid flags.';
webhookService.create.help = 'create(accountId, inboxId) - Creates new webhook in QuePasa with custom trackid and extras.';
webhookService.test.help = 'test(accountId, inboxId, direction="to_chatwoot") - Tests webhook delivery. Direction: "to_chatwoot" or "from_chatwoot".';
webhookService.delete.help = 'delete(accountId, inboxId) - Deletes webhook from QuePasa by trackid or URL.';
webhookService.update.help = 'update(accountId, inboxId) - Updates webhook using N8N middleware (legacy). Sets up bidirectional webhook.';
webhookService.configure.help = 'configure(accountId, inboxId, direction=null) - Syncs webhook. Direction: "to" (QuePasa→Chatwoot), "from" (Chatwoot→QuePasa), null (both). RECOMMENDED.';

export default webhookService;
